if (!customElements.get("custom-purchase")) {
  customElements.define(
    "custom-purchase",
    class CustomPurchase extends HTMLElement {
      constructor() {
        super();
        this.config = null;
        this.selectedVariantId = null;
        this._scrollLocked = false;
        this._scrollY = 0;
      }

      connectedCallback() {
        const configEl = this.querySelector("[data-custom-purchase-config]");
        if (!configEl) return;

        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error("custom-purchase: invalid config JSON", e);
          return;
        }

        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.modalsSection = document.querySelector(
          "#shopify-section-product-modals",
        );
        this.selectBtn = this.querySelector(".custom-purchase__select");
        this.selectLabelEl = this.querySelector("[data-primary-label]");
        this.sizeValueEl = this.querySelector("[data-size-value]");
        this.changeSizeEl = this.querySelector('[data-action="change-size"]');
        this.thumbnailEl = this.querySelector("[data-size-thumbnail]");
        this.thumbnailImgEl = this.querySelector("[data-size-thumbnail-img]");
        this.errorEl = this.querySelector("[data-error-message]");
        this.spinner = this.querySelector(".loading__spinner");

        this.bindPicker();
        this.bindActions();
        this.updateUi();
      }

      ensurePicker() {
        if (!this.picker) {
          this.picker = document.querySelector(
            ".modal-product__custom-purchase",
          );
        }
        return this.picker;
      }

      pickOption(btn) {
        if (!btn || btn.disabled || btn.classList.contains("is-sold-out")) {
          return;
        }

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        const option = this.config?.options?.find(
          (o) => String(o.variantId) === String(variantId),
        );
        if (!option) return;

        this.selectedVariantId = option.variantId;
        this.updateUi();
        this.closePicker();
      }

      bindPicker() {
        if (!this.ensurePicker() || this._pickerBound) return;
        this._pickerBound = true;

        this._onPickerClick = (e) => {
          const optionBtn = e.target.closest(
            '.custom-purchase-picker__option[data-action="pick-option"]',
          );
          if (!optionBtn) return;

          e.preventDefault();
          this.pickOption(optionBtn);
        };

        this.picker.addEventListener("click", this._onPickerClick);

        this.picker.addEventListener("click", (e) => {
          const action = e.target.closest("[data-action]")?.dataset?.action;
          if (action === "open-size-guide") {
            e.preventDefault();
            this.openSizeGuide();
          } else if (action === "open-size-help") {
            e.preventDefault();
            this.openSizeHelp();
          }
        });

        this.picker
          .querySelector(".modal-product__close")
          ?.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.closePicker();
          });

        this.bindStackedModalClose(this.getSizeGuideModal());
        this.bindStackedModalClose(this.getHelpModal());

        document.addEventListener(
          "keydown",
          (this._onKeydown = (e) => {
            if (e.key !== "Escape") return;

            if (this.isModalOpen(this.getHelpModal())) {
              this.closeStackedModal(this.getHelpModal());
              return;
            }

            if (this.isModalOpen(this.getSizeGuideModal())) {
              this.closeStackedModal(this.getSizeGuideModal());
              return;
            }

            if (this.isModalOpen(this.picker)) this.closePicker();
          }),
        );
      }

      bindActions() {
        this.selectBtn?.addEventListener("click", () => {
          if (this.getSelectedOption()) {
            this.addToCart();
          } else {
            this.openPicker();
          }
        });

        this.sizeValueEl?.addEventListener("click", () => {
          if (!this.getSelectedOption()) this.openPicker();
        });

        this.sizeValueEl?.addEventListener("keydown", (e) => {
          if (this.getSelectedOption()) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.openPicker();
          }
        });

        this.changeSizeEl?.addEventListener("click", () => this.openPicker());
      }

      disconnectedCallback() {
        document.removeEventListener("keydown", this._onKeydown);
        if (this.picker && this._onPickerClick) {
          this.picker.removeEventListener("click", this._onPickerClick);
        }
        this._pickerBound = false;
        this.unpinModalsLayer();
        this.unlockPageScroll(true);
      }

      getSizeGuideModal() {
        return document.querySelector(".modal-product__size");
      }

      getHelpModal() {
        return document.querySelector(".modal-product__custom-purchase-help");
      }

      isModalOpen(modal) {
        return (
          modal?.classList.contains("visible") &&
          !modal?.classList.contains("slide-out")
        );
      }

      hasOpenCustomModals() {
        return (
          this.isModalOpen(this.picker) ||
          this.isModalOpen(this.getSizeGuideModal()) ||
          this.isModalOpen(this.getHelpModal())
        );
      }

      lockPageScroll() {
        if (this._scrollLocked) return;
        this._scrollY = window.scrollY;
        document.body.style.setProperty(
          "--custom-purchase-scroll-offset",
          `-${this._scrollY}px`,
        );
        document.documentElement.classList.add("custom-purchase-scroll-locked");
        document.body.classList.add("custom-purchase-scroll-locked");
        this._scrollLocked = true;
      }

      unlockPageScroll(force = false) {
        if (!this._scrollLocked) return;
        if (!force && this.hasOpenCustomModals()) return;

        document.documentElement.classList.remove(
          "custom-purchase-scroll-locked",
        );
        document.body.classList.remove("custom-purchase-scroll-locked");
        document.body.style.removeProperty("--custom-purchase-scroll-offset");
        window.scrollTo(0, this._scrollY || 0);
        this._scrollLocked = false;
      }

      pinModalsLayer() {
        this.modalsSection?.classList.add("custom-purchase-modals-pinned");
      }

      unpinModalsLayer(force = false) {
        if (!force && this.hasOpenCustomModals()) return;
        this.modalsSection?.classList.remove("custom-purchase-modals-pinned");
        this.modalsSection?.style.removeProperty("top");
      }

      clearModalTopStyles() {
        this.picker?.style.removeProperty("top");
        this.getSizeGuideModal()?.style.removeProperty("top");
        this.getHelpModal()?.style.removeProperty("top");
      }

      afterModalClosed() {
        this.getSizeGuideModal()?.classList.remove(
          "modal-product__size--over-picker",
        );
        this.getHelpModal()?.classList.remove(
          "modal-product__custom-purchase-help--over-picker",
        );
        this.clearModalTopStyles();

        if (this.hasOpenCustomModals()) {
          this.lockPageScroll();
          this.pinModalsLayer();
          this.hideThumbnailSlider(true);
          return;
        }

        this.unpinModalsLayer(true);
        this.unlockPageScroll(true);
        document.body.style.overflowY = "";
        this.hideThumbnailSlider(false);
      }

      openPicker() {
        this.bindPicker();
        if (!this.ensurePicker()) return;

        this.lockPageScroll();
        this.pinModalsLayer();
        this.picker.classList.remove("slide-out");
        this.picker.classList.add("visible");
        this.picker.setAttribute("aria-hidden", "false");
        this.picker.style.top = "0";
        this.hideThumbnailSlider(true);
      }

      closePicker() {
        if (!this.picker || !this.isModalOpen(this.picker)) return;

        this.picker.classList.add("slide-out");
        window.clearTimeout(this._closePickerTimer);
        this._closePickerTimer = window.setTimeout(() => {
          this.picker.classList.remove("visible", "slide-out");
          this.picker.setAttribute("aria-hidden", "true");
          this.afterModalClosed();
        }, 1000);
      }

      hideThumbnailSlider(hide) {
        const thumb = document.querySelector(
          ".product--thumbnail_slider .thumbnail-slider",
        );
        if (thumb) thumb.style.display = hide ? "none" : "flex";
      }

      openStackedModal(modal, overPickerClass) {
        if (!modal) return;

        this.lockPageScroll();
        this.pinModalsLayer();
        modal.classList.remove("slide-out");
        modal.classList.add("visible");
        modal.style.top = "0";

        if (this.isModalOpen(this.picker)) {
          modal.classList.add(overPickerClass);
        }

        this.hideThumbnailSlider(true);
      }

      bindStackedModalClose(modal) {
        modal?.querySelector(".modal-product__close")?.addEventListener(
          "click",
          () => {
            if (this.isModalOpen(this.picker)) {
              this.lockPageScroll();
              this.pinModalsLayer();
            }
            window.setTimeout(() => this.afterModalClosed(), 1000);
          },
          true,
        );
      }

      closeStackedModal(modal) {
        if (!this.isModalOpen(modal)) return;
        modal.querySelector(".modal-product__close")?.click();
      }

      openSizeGuide() {
        this.openStackedModal(
          this.getSizeGuideModal(),
          "modal-product__size--over-picker",
        );
      }

      openSizeHelp() {
        this.openStackedModal(
          this.getHelpModal(),
          "modal-product__custom-purchase-help--over-picker",
        );
      }

      getSelectedOption() {
        if (this.selectedVariantId == null || !this.config?.options) {
          return null;
        }
        return (
          this.config.options.find(
            (o) => String(o.variantId) === String(this.selectedVariantId),
          ) || null
        );
      }

      updateUi() {
        const selected = this.getSelectedOption();
        const hasSelection = Boolean(selected);

        if (this.selectBtn && this.selectLabelEl) {
          if (hasSelection) {
            this.selectBtn.dataset.action = "add-to-cart";
            const available = selected.available;
            this.selectBtn.disabled = !available;

            if (!available) {
              this.selectLabelEl.textContent = this.config.strings.soldOut;
            } else if (this.config.purchaseMode === "add_to_cart") {
              this.selectLabelEl.textContent = this.config.strings.addToCart;
            } else {
              this.selectLabelEl.textContent = this.config.strings.preorder;
            }
          } else {
            this.selectBtn.dataset.action = "open-picker";
            this.selectBtn.disabled = false;
            this.selectLabelEl.textContent = this.config.strings.addToCart;
          }
        }

        if (this.sizeValueEl) {
          this.sizeValueEl.classList.toggle("is-selected", hasSelection);
          this.sizeValueEl.textContent = hasSelection
            ? [
                selected.title,
                selected.sizeDescription?.replace(/\s*·\s*/g, ", "),
              ]
                .filter(Boolean)
                .join(" ")
            : this.config.stepLabel || "Select size";

          if (hasSelection) {
            this.sizeValueEl.removeAttribute("role");
            this.sizeValueEl.removeAttribute("tabindex");
            this.sizeValueEl.removeAttribute("aria-label");
            this.sizeValueEl.classList.remove("hover-center");
          } else {
            this.sizeValueEl.setAttribute("role", "button");
            this.sizeValueEl.setAttribute("tabindex", "0");
            this.sizeValueEl.setAttribute(
              "aria-label",
              this.config.stepLabel || "Select size",
            );
            this.sizeValueEl.classList.add("hover-center");
          }
        }

        if (this.changeSizeEl) {
          this.changeSizeEl.hidden = !hasSelection;
        }

        if (this.thumbnailEl && this.thumbnailImgEl) {
          const thumbnail = hasSelection ? selected.thumbnail : "";
          if (thumbnail) {
            this.thumbnailImgEl.src = thumbnail;
            this.thumbnailImgEl.alt = selected.title || "";
            this.thumbnailEl.hidden = false;
          } else {
            this.thumbnailImgEl.removeAttribute("src");
            this.thumbnailImgEl.alt = "";
            this.thumbnailEl.hidden = true;
          }
        }

        this.picker
          ?.querySelectorAll('[data-action="pick-option"]')
          .forEach((btn) => {
            const isSelected =
              String(btn.dataset.variantId) === String(this.selectedVariantId);
            btn.classList.toggle("is-selected", isSelected);
            btn.setAttribute("aria-pressed", isSelected ? "true" : "false");
          });
      }

      setError(message) {
        if (!this.errorEl) return;
        if (message) {
          this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        } else {
          this.errorEl.textContent = "";
          this.errorEl.hidden = true;
        }
      }

      addToCart() {
        const selected = this.getSelectedOption();
        if (!selected || !selected.available) return;
        if (this.selectBtn?.getAttribute("aria-disabled") === "true") return;

        this.setError("");
        this.selectBtn.setAttribute("aria-disabled", "true");
        this.selectBtn.classList.add("loading");
        this.spinner?.classList.remove("hidden");

        const formData = new FormData();
        formData.append("id", String(selected.variantId));
        formData.append("quantity", "1");

        Object.entries(this.config.lineProperties || {}).forEach(
          ([key, value]) => {
            if (value) formData.append(`properties[${key}]`, value);
          },
        );

        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(","),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "custom-purchase",
                productVariantId: String(selected.variantId),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.setError(response.description);
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "custom-purchase",
              productVariantId: String(selected.variantId),
              cartData: response,
            });

            this.error = false;
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
            this.setError(this.config.strings.error || "Could not add to cart");
            this.error = true;
          })
          .finally(() => {
            this.selectBtn.classList.remove("loading");
            if (this.cart?.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.selectBtn.removeAttribute("aria-disabled");
            this.spinner?.classList.add("hidden");
          });
      }
    },
  );
}
